/**
 * What We Do Section - Home Page
 * Scroll-reveal animations with staggered cards
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { capabilities } from '../../data/mock';
import { ArrowUpRight } from 'lucide-react';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../ui/ScrollReveal';

const WhatWeDoSection = () => {
  return (
    <section id="what-we-do" className="py-12 sm:py-16 md:py-20 lg:py-28 bg-[#F5F3F0] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal variant="fadeUp" threshold={0.2}>
          <div className="mb-8 sm:mb-12 md:mb-14">
            <span className="text-[#8B7355] text-xs sm:text-sm font-semibold tracking-widest uppercase">
              What We Do
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] mt-2 sm:mt-3 mb-3 sm:mb-4">
              Comprehensive industrial <span className="italic">solutions</span>
            </h2>
            <p className="text-[#6B6B65] text-base sm:text-lg max-w-2xl">
              From concept to completion, we deliver end-to-end industrial construction services with precision, expertise, and unwavering commitment to quality.
            </p>
          </div>
        </ScrollReveal>

        <StaggerContainer staggerDelay={0.1} threshold={0.1}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {capabilities.map((capability, index) => (
              <StaggerItem key={capability.id}>
                <Link
                  to={`/services#service-${capability.id}`}
                  className="group block p-6 sm:p-8 bg-white hover:bg-[#1A1A1A] transition-all duration-500 h-full relative overflow-hidden"
                  style={{ transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }}
                >
                  <span className="absolute top-0 right-0 w-20 h-20 sm:w-24 sm:h-24 bg-[#8B7355]/5 rounded-bl-full transition-transform duration-500 group-hover:scale-150" />
                  <span className="text-3xl sm:text-4xl font-light text-[#8B7355] group-hover:text-white/30 mb-3 sm:mb-4 block transition-colors">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-serif text-lg sm:text-xl text-[#1A1A1A] group-hover:text-white mb-2 sm:mb-3 transition-colors">
                    {capability.title}
                  </h3>
                  <p className="text-[#6B6B65] group-hover:text-white/70 text-xs sm:text-sm leading-relaxed transition-colors">
                    {capability.description}
                  </p>
                  <ArrowUpRight className="mt-3 sm:mt-4 w-4 h-4 sm:w-5 sm:h-5 text-[#8B7355] opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              </StaggerItem>
            ))}
          </div>
        </StaggerContainer>

        <ScrollReveal variant="fadeUp" threshold={0.2} delay={0.3}>
          <div className="mt-8 sm:mt-12 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm sm:text-base text-[#8B7355] font-medium hover:gap-3 transition-all group"
            >
              Explore our services
              <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
